/**
 * What a thread shows for its unfinished turns (slice 1 spec §7.3), and
 * where. Finished turns speak through their messages: the reply, the
 * "No reply." line, or the failure line (§3.2).
 */
import type { AiStatus, ComputerStatus } from "../../app/me";
import { briefNotes } from "../areas/brief";
import { memoryNote } from "../memory/memory-view";
import type { ProgressFrame, ThreadMessage, Turn, TurnTools } from "./api";
import { isLocalTurn } from "./outbox";
import type { TurnLive, ViewMessage } from "./projection";
import type { Capture } from "./made-view";

export type TurnLine =
  /** A reply this tab is still sending: its local turn (`outbox.ts`). */
  | { kind: "sending"; text: "Sending…" }
  | { kind: "queued"; text: "Queued. Answered next." }
  /** Next, and someone holds the computer (slice 6c §5): it waits for them. */
  | { kind: "held"; text: string }
  /** Next, while an update runs (slice 1j §6.2): it waits for it, unclaimed. */
  | { kind: "updating"; text: "Queued until the update is done." }
  | { kind: "waking"; text: "Waking your computer…" }
  | {
      kind: "setting_up";
      text: "Your computer is still being set up. This will run when it's ready.";
    }
  /**
   * The working state; `progress` is the latest line. `onComputer`: the agent
   * is using the computer's browser, so Watch shows beside it (slice 6c §4).
   */
  | {
      kind: "working";
      progress: string | null;
      onComputer?: boolean;
      /** Slice 6f: the browser's latest picture, live on the computer, folded after. */
      frame?: ProgressFrame;
    };

const byAge = (a: Turn, b: Turn) =>
  a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);

/** A real turn is ahead of this one: running, or queued and older. A send still in this tab isn't. */
function isAhead(turn: Turn, turns: Turn[]): boolean {
  return turns.some(
    (t) =>
      t.id !== turn.id &&
      !isLocalTurn(t) &&
      (t.status === "running" || (t.status === "queued" && byAge(t, turn) < 0)),
  );
}

/** Who holds the computer (slice 6c §5): its turns wait until they're done. */
export type Holder = { name: string; you: boolean };

const heldText = (h: Holder) =>
  h.you ? "Waiting: you have the computer" : `Waiting: ${h.name} is using the computer`;

export function turnLine(
  turn: Turn,
  turns: Turn[],
  computer: ComputerStatus | null,
  holder: Holder | null = null,
  updating = false,
): TurnLine {
  if (isLocalTurn(turn)) return { kind: "sending", text: "Sending…" };
  // Slice 1i: a person pressed Stop and the run is being stopped.
  if (turn.stop_requested_at) return { kind: "working", progress: "Stopping…" };
  if (turn.status === "running") {
    const progress = turn.progress?.text ?? null;
    const frame = turn.progress?.frame;
    const line: TurnLine = turn.progress_on_computer
      ? { kind: "working", progress, onComputer: true }
      : { kind: "working", progress };
    return frame ? { ...line, frame } : line;
  }
  if (isAhead(turn, turns)) return { kind: "queued", text: "Queued. Answered next." };
  if (holder) return { kind: "held", text: heldText(holder) };
  if (updating) return { kind: "updating", text: "Queued until the update is done." };
  if (computer === "waking") return { kind: "waking", text: "Waking your computer…" };
  if (computer === "setting_up" || computer === "waiting") {
    return {
      kind: "setting_up",
      text: "Your computer is still being set up. This will run when it's ready.",
    };
  }
  return { kind: "working", progress: null };
}

export type Row =
  | { kind: "message"; key: string; message: ViewMessage }
  | { kind: "turn"; key: string; turn: Turn; line: TurnLine }
  /**
   * S-509: a finished turn's fold line ("Worked for 37s · 9 steps"), after
   * its last row. `quiet`: a check-in's or a follow-through's, said quieter.
   */
  | {
      kind: "work";
      key: string;
      turn: Turn;
      quiet: boolean;
      /** The turn's task, said as a status on the line instead of a card ("Task · Done"). */
      task?: string;
    }
  /** What the turn made, one list under its reply: its docs and a new Area. */
  | { kind: "made"; key: string; turnId: string; items: MadeItem[] };

export type MadeItem =
  /** `capture` (S-525): a page `browser_save` kept, as its card's data says. */
  | { kind: "doc"; key: string; docId: string; title: string; capture: Capture | null }
  | { kind: "area"; key: string; slug: string; label: string }
  /** S-525: a routine the turn made or changed; `line` its line's words, until the routine loads. */
  | { kind: "routine"; key: string; routineId: string; changed: boolean; line: string };

/** What starts a turn the crew does on its own, in a task's chat (slice 2b): said quieter (S-509). */
const BACKGROUND = new Set(["nudge", "follow_through"]);

function quietTurn(turn: Turn, messages: ViewMessage[]): boolean {
  const trigger = messages.find((m) => m.id === turn.trigger_message_id);
  return trigger?.kind === "system" && BACKGROUND.has(String(trigger.data.event));
}

const hasWork = (turn: Turn) =>
  (turn.status === "done" || turn.status === "failed") && (turn.work?.steps ?? 0) > 0;

/**
 * The thread's rows: its messages, with each unfinished turn's line after the
 * last message that belongs to it (its trigger, or its reply so far).
 */
export function threadRows(
  messages: ViewMessage[],
  turns: Turn[],
  computer: ComputerStatus | null,
  holder: Holder | null = null,
  updating = false,
): Row[] {
  const rows: Row[] = messages.map((m) => ({ kind: "message", key: m.key, message: m }));
  const open = turns.filter((t) => t.status === "queued" || t.status === "running").sort(byAge);
  // Insert from the back, so earlier indexes stay put; same-anchor turns keep their age order.
  for (const turn of [...open].reverse()) {
    let anchor = -1;
    messages.forEach((m, i) => {
      if (m.id === turn.trigger_message_id || m.turn_id === turn.id) anchor = i;
    });
    const at = anchor === -1 ? rows.length : rowIndexAfter(rows, messages[anchor].key);
    rows.splice(at, 0, {
      kind: "turn",
      key: `turn:${turn.id}`,
      turn,
      line: turnLine(turn, turns, computer, holder, updating),
    });
  }
  // S-509: each finished turn's fold, after the last of its rows that's loaded.
  for (const turn of turns.filter(hasWork)) {
    let anchor = -1;
    messages.forEach((m, i) => {
      if (m.turn_id === turn.id) anchor = i;
    });
    if (anchor === -1) continue;
    rows.splice(rowIndexAfter(rows, messages[anchor].key), 0, {
      kind: "work",
      key: `work:${turn.id}`,
      turn,
      quiet: quietTurn(turn, messages),
    });
  }
  return rows;
}

/** What a turn block needs of a thread's task: whether it's done. */
type BlockTask = { id: string; stage: string };

const CARDS = new Set(["approval_card", "handoff_card", "task_card", "choice_card"]);

/** S-525: a person's tap on a question's answer, shown by its card, not as a message. */
const isChoiceAnswer = (m: { data: Record<string, unknown> }) =>
  typeof m.data.choice_for === "string";

const turnOf = (row: Row): string | null =>
  row.kind === "message" ? row.message.turn_id : row.kind === "made" ? row.turnId : row.turn.id;

/**
 * The turn's task to say as a status on its line, not a card: the one it
 * made for the chat's own ask (the turn answering the opener), else one it
 * made and finished. Any other task keeps its card.
 */
function foldedTask(
  cards: ViewMessage[],
  tasks: Map<string, BlockTask>,
  answersOpener: boolean,
): string | null {
  // Only a task the thread has: its status needs the task, else the card stays (an older Worker).
  const made = cards.filter(
    (m) => m.kind === "task_card" && m.author_agent_id && tasks.has(String(m.data.task_id)),
  );
  const done = made.find((m) => tasks.get(String(m.data.task_id))?.stage === "done");
  const card = answersOpener && made.length === 1 ? made[0] : done;
  return card ? String(card.data.task_id) : null;
}

/** A turn's area line, as a Made item. */
function madeArea(m: ViewMessage): MadeItem | null {
  if (m.kind !== "system" || m.data.event !== "area_created") return null;
  const slug = m.data.slug;
  if (typeof slug !== "string") return null;
  return { kind: "area", key: m.key, slug, label: m.body.replace(/^Made the area /, "") };
}

/** Where a turn's message goes in its block; a doc or a new Area is a Made item. */
type Place = "reply" | "card" | "line" | "drop" | MadeItem;

const ROUTINE_MADE = new Set(["routine_created", "routine_changed"]);

/** S-525: a turn's "Made a routine" or "Changed …" line, as a Made item. */
function madeRoutine(m: ViewMessage): MadeItem | null {
  if (m.kind !== "system" || !ROUTINE_MADE.has(String(m.data.event))) return null;
  const id = m.data.routine_id;
  if (typeof id !== "string" || !id) return null;
  const changed = m.data.event === "routine_changed";
  return { kind: "routine", key: m.key, routineId: id, changed, line: m.body };
}

/** A doc card's `capture` (S-525), when it's one: a screenshot or a PDF kept. */
function captureOf(raw: unknown): Capture | null {
  const c = raw as Partial<Capture> | null | undefined;
  if (!c || (c.as !== "screenshot" && c.as !== "pdf")) return null;
  return {
    as: c.as,
    site: typeof c.site === "string" ? c.site : null,
    pages: typeof c.pages === "number" ? c.pages : null,
    file_id: typeof c.file_id === "string" ? c.file_id : null,
  };
}

/** A doc, a new Area or a routine the turn made, as a Made item. */
function madeItem(m: ViewMessage): MadeItem | null {
  if (m.kind !== "doc_card") return madeArea(m) ?? madeRoutine(m);
  const capture = captureOf(m.data.capture);
  return { kind: "doc", key: m.key, docId: String(m.data.doc_id), title: m.body, capture };
}

/** A line or card that says again what the block shows: an edit of a doc it made, its folded task. */
const isEcho = (m: ViewMessage, madeDocs: Set<string>, task: string | null) =>
  (m.kind === "system" && m.data.event === "doc_updated" && madeDocs.has(String(m.data.doc_id))) ||
  (m.kind === "task_card" && String(m.data.task_id) === task);

function placeOf(m: ViewMessage, madeDocs: Set<string>, task: string | null): Place {
  const item = madeItem(m);
  if (item) return item;
  if (isEcho(m, madeDocs, task)) return "drop";
  if (m.kind === "system") return "line";
  return CARDS.has(m.kind) ? "card" : "reply";
}

/** The turn's work line, with its task as a status; one is made for a stepless finished turn's task. */
function workEnd(
  work: Extract<Row, { kind: "work" }> | undefined,
  turn: Turn | undefined,
  task: string | null,
): Row[] {
  if (!task) return work ? [work] : [];
  if (work) return [{ ...work, task }];
  return turn ? [{ kind: "work", key: `work:${turn.id}`, turn, quiet: false, task }] : [];
}

const finished = (turn: Turn | undefined): turn is Turn =>
  turn?.status === "done" || turn?.status === "failed";

const madeRow = (items: MadeItem[], id: string): Row[] =>
  items.length ? [{ kind: "made", key: `made:${id}`, turnId: id, items }] : [];

/** The turn answers the thread's opening message: its task is the chat's own ask. */
const answers = (turn: Turn | undefined, openerId: string | null) =>
  openerId !== null && turn?.trigger_message_id === openerId;

/**
 * One turn's rows as one block: the reply first, then the cards that ask
 * something (approval, handoff, a task that isn't the chat's own), then one
 * Made list (its docs and a new Area), then its other lines, then its fold or
 * live line.
 */
function turnBlock(
  rows: Row[],
  turn: Turn | undefined,
  tasks: Map<string, BlockTask>,
  openerId: string | null,
): Row[] {
  const messages = rows.flatMap((r) => (r.kind === "message" ? [r.message] : []));
  const madeDocs = new Set(
    messages.filter((m) => m.kind === "doc_card").map((m) => String(m.data.doc_id)),
  );
  // While the turn runs its line is the live one, so the card stays until the fold can say it.
  const task = finished(turn) ? foldedTask(messages, tasks, answers(turn, openerId)) : null;
  const placed = { reply: [] as Row[], card: [] as Row[], line: [] as Row[], drop: [] as Row[] };
  const items: MadeItem[] = [];
  for (const r of rows) {
    if (r.kind !== "message") continue;
    const place = placeOf(r.message, madeDocs, task);
    if (typeof place === "string") placed[place].push(r);
    else items.push(place);
  }
  const made = madeRow(items, turn?.id ?? turnOf(rows[0]) ?? "");
  const live = rows.filter((r) => r.kind === "turn");
  const work = rows.find((r): r is Extract<Row, { kind: "work" }> => r.kind === "work");
  return [
    ...placed.reply,
    ...placed.card,
    ...made,
    ...placed.line,
    ...live,
    ...workEnd(work, turn, task),
  ];
}

/**
 * Each turn's rows gathered into one block, where its first row was, so a
 * turn shows one header (`continuesRow`) and its reply leads.
 */
export function turnBlocks(
  rows: Row[],
  turns: Map<string, Turn>,
  tasks: Map<string, BlockTask>,
  openerId: string | null,
): Row[] {
  const byTurn = new Map<string, Row[]>();
  for (const r of rows) {
    const id = turnOf(r);
    if (id) byTurn.set(id, [...(byTurn.get(id) ?? []), r]);
  }
  const placed = new Set<string>();
  return rows.flatMap((r) => {
    const id = turnOf(r);
    if (!id) return [r];
    if (placed.has(id)) return [];
    placed.add(id);
    return turnBlock(byTurn.get(id) ?? [], turns.get(id), tasks, openerId);
  });
}

/**
 * Earlier pages' messages as rows, minus any the loaded window now holds (new
 * messages shift the window of the latest 200), and the id to page back from.
 */
export function olderThan(
  earlier: ThreadMessage[],
  latest: ThreadMessage[],
): { rows: Array<Row & { kind: "message" }>; before: string } {
  const loaded = new Set(latest.map((m) => m.id));
  const older = earlier.filter((m) => !loaded.has(m.id));
  const rows = older
    // A tap shows on its card, on an older page too (Devin on #535).
    .filter((m) => !isChoiceAnswer(m))
    .map((m) => ({ kind: "message" as const, key: m.id, message: { ...m, key: m.id } }));
  return { rows, before: older[0]?.id ?? latest[0]?.id ?? "" };
}

/** A follow-up waiting on the composer, above the reply box, rather than in the transcript. */
export type ComposerQueueItem = {
  message: ViewMessage;
  /** Its turn. A send that failed has none yet. */
  turn: Turn | null;
  /** "Queued. Answered next." or "Sending…". None for a send that failed. */
  line: TurnLine | null;
};

const openTurn = (turn: Turn) =>
  isLocalTurn(turn) || turn.status === "queued" || turn.status === "running";

type QueueMessage = ViewMessage & { unsent?: "sending" | "failed" };

/**
 * A real turn is open for something else. A send whose answer was lost may
 * have landed: its own turn, found by its id, isn't one it waits behind.
 */
const openForOther = (message: QueueMessage, turns: Turn[]) =>
  turns.some(
    (t) =>
      !isLocalTurn(t) &&
      t.trigger_message_id !== message.id &&
      (t.status === "running" || t.status === "queued"),
  );

/** A person's text still waiting, or a send that failed while another turn is open. */
function queueItem(
  message: QueueMessage,
  turns: Turn[],
  computer: ComputerStatus | null,
): ComposerQueueItem | null {
  if (message.kind !== "text" || message.author_agent_id) return null;
  if (message.unsent === "failed") {
    return openForOther(message, turns) ? { message, turn: null, line: null } : null;
  }
  const turn = turns
    .filter((t) => t.trigger_message_id === message.id && openTurn(t))
    .sort(byAge)
    .at(-1);
  if (!turn) return null;
  const line = turnLine(turn, turns, computer);
  const waiting = line.kind === "queued" || (line.kind === "sending" && isAhead(turn, turns));
  return waiting ? { message, turn, line } : null;
}

/**
 * Follow-ups that belong on the composer: a message queued behind a turn
 * already going, one this tab is still sending into that wait, and a send
 * that failed while another turn is still open. Oldest first. The transcript
 * leaves them out until a turn picks them up.
 */
export function composerQueue(
  messages: QueueMessage[],
  turns: Turn[],
  computer: ComputerStatus | null,
): ComposerQueueItem[] {
  return messages.flatMap((message) => {
    const item = queueItem(message, turns, computer);
    return item ? [item] : [];
  });
}

export type ThreadLayout = {
  /** The thread's first message, shown in a band above the replies; none when older ones aren't loaded. */
  opener: ViewMessage | undefined;
  rows: Row[];
  /** Follow-ups shown on the composer instead of in the rows. */
  queue: ComposerQueueItem[];
  /** A turn is running, so a reply now is queued. */
  busy: boolean;
  /**
   * The turn being answered, which Stop stops (slice 1i spec): running, or
   * next and not waiting behind another. Null when nothing is.
   */
  stoppable: Turn | null;
  turns: Map<string, Turn>;
};

/** How a loaded thread lays out: the opener, then every other row. */
export function threadLayout(
  detail: { has_more: boolean; turns: Turn[]; tasks?: BlockTask[] },
  all: ViewMessage[],
  computer: { status: ComputerStatus; ai?: { updating?: boolean } } | null,
  holder: Holder | null = null,
): ThreadLayout {
  // S-525: a tap's message is its question card's answer, which the card shows.
  const messages = all.filter((m) => !isChoiceAnswer(m));
  const updating = computer?.ai?.updating === true;
  const opener = detail.has_more ? undefined : messages[0];
  const queue = composerQueue(messages, detail.turns, computer?.status ?? null).filter(
    (item) => item.message !== opener,
  );
  const waiting = new Set(queue.map((item) => item.message.key));
  const waitingTurns = new Set(queue.flatMap((item) => (item.turn ? [item.turn.id] : [])));
  const status = computer?.status ?? null;
  const listed = threadRows(messages, detail.turns, status, holder, updating).filter((r) => {
    if (r.kind === "message") return r.message !== opener && !waiting.has(r.message.key);
    return r.kind === "made" || !waitingTurns.has(r.turn.id);
  });
  const turns = new Map(detail.turns.map((t) => [t.id, t]));
  const tasks = new Map((detail.tasks ?? []).map((t) => [t.id, t]));
  const rows = turnBlocks(listed, turns, tasks, opener?.id ?? null);
  return {
    opener,
    rows,
    queue,
    busy: detail.turns.some((t) => t.status === "running"),
    // During an update nothing is answered until it ends (slice 1j §6.2): only a running turn stops.
    stoppable: updating
      ? (detail.turns.find((t) => t.status === "running" && !isLocalTurn(t)) ?? null)
      : stoppableTurn(detail.turns),
    turns,
  };
}

/**
 * The turn Stop acts on (slice 1i spec): the oldest real one being answered.
 * One this tab is still sending isn't a turn yet; one queued behind another
 * is taken back from the tray instead.
 */
export function stoppableTurn(turns: Turn[]): Turn | null {
  const open = turns.filter(
    (t) => !isLocalTurn(t) && (t.status === "running" || t.status === "queued"),
  );
  return open.sort(byAge).find((t) => t.status === "running" || !isAhead(t, open)) ?? null;
}

/**
 * A row that follows on from the one above (slice 2b spec §5): the same
 * agent's message in the same turn, a reply's later part or a card under it.
 * A turn shows one header (`turnBlocks`), so its avatar and header aren't repeated.
 */
export function continuesRow(prev: Row | undefined, row: Row): boolean {
  if (prev?.kind !== "message" || row.kind !== "message") return false;
  const a = prev.message;
  const b = row.message;
  if (!a.author_agent_id || a.author_agent_id !== b.author_agent_id) return false;
  return Boolean(a.turn_id) && a.turn_id === b.turn_id;
}

/** A `turn_failed` or `turn_interrupted` line (§3.2). */
export function isFailureLine(message: ViewMessage): boolean {
  const event = message.data.event;
  return message.kind === "system" && (event === "turn_failed" || event === "turn_interrupted");
}

/**
 * The line of a turn that failed `ai_auth` (slice 1b spec §5) offers
 * Reconnect until the AI is connected again; then the turn's Retry takes over.
 */
export function offersReconnect(
  message: ViewMessage,
  ai: AiStatus | undefined,
  updating = false,
): boolean {
  // Not during an update (slice 1j §6.6): it may be the reconnect.
  if (updating) return false;
  return isFailureLine(message) && message.data.reason === "ai_auth" && ai !== "connected";
}

/** The turn a failure line offers Retry for, while `can_retry` holds (§3.2). */
export function retryableTurn(message: ViewMessage, turns: Map<string, Turn>): Turn | undefined {
  if (!isFailureLine(message)) return undefined;
  const turn = turns.get(String(message.data.turn_id));
  return turn?.can_retry ? turn : undefined;
}

/**
 * The quiet line under a reply a fallback answered (slice 1c spec §5.4,
 * §6.3): "Answered by GPT-5.5, a fallback", under the turn's last reply part
 * only, as the thread API names it (`last_reply_id`), so a reply split across
 * a page boundary gets it once. Null for anything else.
 */
export function fallbackNote(message: ViewMessage, turns: Map<string, Turn>): string | null {
  if (message.kind !== "text" || !message.author_agent_id || !message.turn_id) return null;
  const turn = turns.get(message.turn_id);
  if (!turn?.fallback || turn.last_reply_id !== message.id) return null;
  return `Answered by ${turn.fallback.label}, a fallback`;
}

/**
 * "Updated its notes" (slice 1d spec §6.2), under the last reply part of a
 * turn that changed the agent's notes or the workspace's context, linking to
 * the file on the Memory page. Null for anything else.
 */
export function memoryNoteFor(
  message: ViewMessage,
  turns: Map<string, Turn>,
  agents: Array<{ id: string; slug: string }>,
): { text: string; href: string } | null {
  if (message.kind !== "text" || !message.author_agent_id || !message.turn_id) return null;
  const turn = turns.get(message.turn_id);
  if (!turn?.updated_files?.length || turn.last_reply_id !== message.id) return null;
  const slug = agents.find((a) => a.id === turn.agent_id)?.slug ?? null;
  return memoryNote(turn.updated_files, slug);
}

/**
 * "Updated the Garage gym brief" (slice 6d spec §8.5), under the same reply
 * part, for each Area whose brief the turn changed (`area:<id>` in
 * `updated_files`), linking to its editor.
 */
export function briefNotesFor(
  message: ViewMessage,
  turns: Map<string, Turn>,
  areas: ReadonlyArray<{ id: string; slug: string; label: string }>,
): { text: string; href: string }[] {
  if (message.kind !== "text" || !message.author_agent_id || !message.turn_id) return [];
  const turn = turns.get(message.turn_id);
  if (!turn?.updated_files?.length || turn.last_reply_id !== message.id) return [];
  return briefNotes(turn.updated_files, areas);
}

/**
 * "Texted to you" (slice 5 spec §11.4), under the last reply part of a
 * routine's run that was texted to its person, or "Emailed to you" (slice 2h
 * §8.3) under a reply in an email chat once it's sent: "… to Sam" for anyone
 * else. Null for anything else.
 */
export function textedNoteFor(
  message: ViewMessage,
  turns: Map<string, Turn>,
  viewerId: string,
): string | null {
  if (message.kind !== "text" || !message.author_agent_id || !message.turn_id) return null;
  const turn = turns.get(message.turn_id);
  const to = turn?.texted_to ?? turn?.emailed_to;
  if (!turn || !to || turn.last_reply_id !== message.id) return null;
  const verb = turn.texted_to ? "Texted" : "Emailed";
  if (to.id === viewerId) return `${verb} to you`;
  return `${verb} to ${to.name.split(/\s+/)[0] || to.name}`;
}

export type ToolsLine =
  /** "Searched the web 3 times · read 2 pages": the lookups that worked. */
  | { kind: "lookups"; text: string }
  | { kind: "capped"; text: "Web lookups used up for today · back at midnight" };

const times = (n: number) => (n === 1 ? "once" : `${n} times`);
const pages = (n: number) => (n === 1 ? "a page" : `${n} pages`);

/**
 * The lines a finished reply gets for its turn's web lookups (slice 2a spec
 * §5): the counts of those that worked, then the copper "used up" line when
 * one was refused for the day's cap. None when it looked nothing up; a
 * missing `tools` (an older turn) is none.
 */
export function toolsLines(tools: TurnTools | null | undefined): ToolsLine[] {
  const searches = Math.max(0, tools?.search_web ?? 0);
  const reads = Math.max(0, tools?.read_page ?? 0);
  const lines: ToolsLine[] = [];
  const parts = [
    searches > 0 ? `searched the web ${times(searches)}` : null,
    reads > 0 ? `read ${pages(reads)}` : null,
  ].filter((p): p is string => p !== null);
  if (parts.length > 0) {
    const text = parts.join(" · ");
    lines.push({ kind: "lookups", text: text[0].toUpperCase() + text.slice(1) });
  }
  if (tools?.capped) {
    lines.push({ kind: "capped", text: "Web lookups used up for today · back at midnight" });
  }
  return lines;
}

/**
 * The web lookup lines under the last reply part of a finished turn, as the
 * thread API names it (`last_reply_id`), like the fallback line. Empty for
 * anything else, and while the turn runs (its progress line speaks then).
 */
export function toolsLinesFor(message: ViewMessage, turns: Map<string, Turn>): ToolsLine[] {
  if (message.kind !== "text" || !message.author_agent_id || !message.turn_id) return [];
  const turn = turns.get(message.turn_id);
  if (!turn || turn.last_reply_id !== message.id) return [];
  if (turn.status === "queued" || turn.status === "running") return [];
  // S-509: the fold says what it looked up; only the day's cap still has a line.
  if (hasWork(turn)) return toolsLines(turn.tools).filter((l) => l.kind === "capped");
  return toolsLines(turn.tools);
}

function rowIndexAfter(rows: Row[], messageKey: string): number {
  const i = rows.findIndex((r) => r.kind === "message" && r.key === messageKey);
  return i === -1 ? rows.length : i + 1;
}

/** Slice 1q §6.3: what the Working line says for a live state, or null for the progress line. */
export function liveLine(live: TurnLive | null): string | null {
  if (live?.state === "thinking") return "Thinking…";
  if (live?.state === "writing") return "Writing…";
  if (live?.state === "retrying") {
    return live.attempt && live.of
      ? `Trying again (${live.attempt} of ${live.of})…`
      : "Trying again…";
  }
  return null;
}
